'use client';
import React, { useState } from 'react';

interface Plan {
  name: string;
  monthlyPrice: string;
  annualPrice: string;
  description: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  accent: string;
  textColor: string;
}

const plans: Plan[] = [
  {
    name: 'Starter',
    monthlyPrice: '$49',
    annualPrice: '$39',
    description: 'Perfect for individual sellers or small brands just getting started with live commerce.',
    features: [
      'Up to 3 active job applications',
      'Basic seller/brand profile',
      'Access to job board',
      'Standard matching',
      'In-app messaging',
    ],
    cta: 'Get Started Free',
    highlighted: false,
    accent: 'bg-neutral-01',
    textColor: 'text-dark',
  },
  {
    name: 'Pro',
    monthlyPrice: '$149',
    annualPrice: '$119',
    description: 'For serious sellers and growing brands who want priority access and advanced analytics.',
    features: [
      'Unlimited job applications',
      'Verified profile badge',
      'Priority matching & discovery',
      'Full analytics dashboard',
      'Contract & escrow management',
      'Dedicated account manager',
    ],
    cta: 'Start Pro Trial',
    highlighted: true,
    accent: 'bg-dark',
    textColor: 'text-white',
  },
  {
    name: 'Enterprise',
    monthlyPrice: '$499',
    annualPrice: '$399',
    description: 'For brands managing multiple live sellers and agencies running large-scale campaigns.',
    features: [
      'Unlimited seller seats',
      'Multi-seller campaign management',
      'Custom analytics & reporting',
      'API access & integrations',
      'White-glove onboarding',
      '24/7 priority support',
    ],
    cta: 'Contact Sales',
    highlighted: false,
    accent: 'bg-neutral-01',
    textColor: 'text-dark',
  },
];

export default function PricingSection() {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-14">
          <div className="mb-5">
            <div
              className="bg-theme-02 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
              style={{ transform: 'rotate(-6deg)' }}
            >
              Pricing
            </div>
          </div>
          <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-4">
            Flexible pricing plans
          </h2>
          <p className="text-[18px] text-dark/60 max-w-[560px] mx-auto leading-relaxed">
            Choose a plan that fits your needs. Scale up as your live commerce business grows.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`text-[15px] font-semibold ${!isAnnual ? 'text-dark' : 'text-dark/40'}`}>Monthly</span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
              isAnnual ? 'bg-dark' : 'bg-neutral-02'
            }`}
          >
            <div
              className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-300 ${
                isAnnual ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-[15px] font-semibold ${isAnnual ? 'text-dark' : 'text-dark/40'}`}>Annual</span>
          <span className="text-[12px] font-bold text-dark bg-theme-02 px-3 py-1 rounded-full">Save 20%</span>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Most popular label */}
          <div className="hidden md:flex absolute -top-4 left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-2">
              <svg width="40" height="20" viewBox="0 0 60 20" fill="none">
                <path d="M60 10 Q30 0 0 10" stroke="#120A0B" strokeWidth="1.5" fill="none" strokeDasharray="4 2"/>
                <polygon points="5,6 0,10 5,14" fill="#120A0B"/>
              </svg>
              <span className="font-kalam text-[16px] text-dark">Most popular</span>
            </div>
          </div>

          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`${plan.accent} rounded-[32px] p-8 md:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                plan.highlighted ? 'ring-2 ring-dark' : ''
              }`}
            >
              {/* Plan name */}
              <div className="mb-6">
                <p className={`text-[14px] font-semibold ${plan.textColor} opacity-60 mb-2`}>{plan.name}</p>
                <div className="flex items-end gap-2 mb-2">
                  <h3 className={`text-[40px] font-bold ${plan.textColor} leading-none`}>
                    {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </h3>
                  <span className={`text-[14px] ${plan.textColor} opacity-50 mb-1`}>/month</span>
                </div>
                <p className={`text-[14px] ${plan.textColor} opacity-60 leading-relaxed`}>{plan.description}</p>
              </div>

              {/* Features */}
              <div className="flex-1 mb-8">
                <div className="space-y-3">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.highlighted ? 'bg-theme-02' : 'bg-theme-02'
                      }`}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="#120A0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <p className={`text-[14px] ${plan.textColor} ${plan.highlighted ? 'opacity-80' : 'opacity-70'}`}>
                        {feature}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <a
                href="#"
                className={`font-bold text-[15px] text-center py-4 rounded-pill transition-all duration-200 ${
                  plan.highlighted
                    ? 'bg-theme-02 text-dark hover:brightness-95' :'bg-dark text-white hover:bg-opacity-80'
                }`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className="text-center text-[14px] text-dark/40 mt-8">
          All plans include a 14-day free trial. No credit card required.
        </p>
      </div>
    </section>
  );
}
